import { useEffect, useMemo, useState } from 'react';
import { Col, DatePicker, Form, Input, message, Modal, Row, Select, Spin } from 'antd';
import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import {
    CustomerStatusEnumerator,
    CustomerTypeEnum,
    GenderEnumeratorFrontend,
    IUserDTO,
    IUserEntity,
    KeyValueRecord,
    removeEmptyValues,
    removeWhiteSpaceFromAllProperties,
} from '@rasayi-workspace/shared';
import { GetItem, PostItem, PutItem } from '@services';
import { AddEditModalProps, ErrorResponse } from '@interfaces';
import dayjs from 'dayjs';
import { State, City } from 'country-state-city';
import HeadingTypography from '../../shared/Typography/Heading';

export const UserAddEditModalComponent = ({ onClose, onSubmit, editMode = false, id: userId = '' }: AddEditModalProps<IUserEntity>) => {
    const { Item } = Form;

    const [form] = Form.useForm<Partial<IUserDTO>>();
    const [messageApi, contextHolder] = message.useMessage();
    const [isUserFetching, setIsUserFetching] = useState(false);
    const [selectedProvince, setSelectedProvince] = useState('');
    const { mutate: createUser, isLoading } = useMutation<IUserEntity, AxiosError>({
        mutationKey: ['createUser'],
        mutationFn: async () => {
            const payload = removeWhiteSpaceFromAllProperties(removeEmptyValues(form.getFieldsValue())) as Partial<IUserDTO>;
            payload.customer_type = CustomerTypeEnum.CASH;
            return PostItem<IUserEntity, IUserDTO>('user/addCustomer', payload);
        },
        onSuccess: async (item: IUserEntity): Promise<void> => onSubmit(item),
        onError: (error: AxiosError) => {
            messageApi.open({
                type: 'error',
                content: (error?.response?.data as ErrorResponse)?.message || 'Contact support for details',
            });
        },
    });

    const { mutate: updateUser, isLoading: isUserUpdating } = useMutation<IUserEntity, AxiosError>({
        mutationKey: ['updateUser'],
        mutationFn: async () => PutItem<IUserEntity, IUserDTO>('user', userId, removeWhiteSpaceFromAllProperties(removeEmptyValues(form.getFieldsValue())) as Partial<IUserDTO>),
        onSuccess: async (item: IUserEntity): Promise<void> => onSubmit(item),
        onError: (error: AxiosError) => {
            messageApi.open({
                type: 'error',
                content: (error?.response?.data as ErrorResponse)?.message || 'Contact support for details',
            });
        },
    });

    const fetchUser = async (userId: string) => {
        try {
            setIsUserFetching(true);
            const result = await GetItem<IUserEntity>('user', userId || '', ['roles', 'groups'], ['roles.id', 'roles.name', 'groups.id', 'groups.name']);

            const customer = {
                first_name: result.first_name || '',
                last_name: result.last_name || '',
                email: result.email || '',
                primary_mobile: result.primary_mobile,
                secondary_mobile: result.secondary_mobile,
                cnic_number: result.cnic_number,
                dob: result.dob ? dayjs(result.dob) : null,
                gender: result.gender || null,
                province: result.province || '',
                city: result.city || '',
                address_line1: result.address_line1 || '',
                address_line2: result.address_line2 || '',
                customer_status: result.customer_status,
            };

            form.setFieldsValue(customer as any);
            setIsUserFetching(false);
        } catch (error) {
            setIsUserFetching(false);
        }
    };

    const handleFormChange = (_: KeyValueRecord, values: Partial<IUserDTO>) => {
        if (values.province !== selectedProvince) setSelectedProvince(values.province || '');
    };

    const onOkAddButtonClickHandler = async () =>
        await form
            .validateFields()
            .then(() => {
                createUser();
            })
            .catch(() => {});

    const onOkEditButtonClickHandler = async () =>
        await form
            .validateFields()
            .then(() => updateUser())
            .catch(() => {});

    useEffect(() => {
        if (editMode && userId) {
            fetchUser(userId);
        }
    }, [editMode, userId]);

    const genderOptions = Object.values(GenderEnumeratorFrontend).map(gender => ({
        label: gender,
        value: gender,
    }));

    const customerStatusOptions = Object.values(CustomerStatusEnumerator).map(status => ({
        label: status,
        value: status,
    }));

    const pakStates = useMemo(
        () =>
            State.getStatesOfCountry('PK').map(state => {
                return {
                    value: state.name,
                    label: state.name,
                    code: state.isoCode,
                };
            }),
        [State]
    );

    const provinceCities = useMemo(() => {
        const selectedState = pakStates.find(state => state.label === selectedProvince);
        if (selectedState) {
            return City.getCitiesOfState('PK', selectedState.code).map(state => {
                return {
                    value: state.name,
                    label: state.name,
                };
            });
        }
        return [];
    }, [selectedProvince]);    

    return (
        <>
            {contextHolder}
            <Modal
                title={!editMode ? 'Add Customer' : 'Edit Customer'}
                open={true}
                confirmLoading={isLoading  || isUserUpdating}
                okText={!editMode ? 'Add Customer' : 'Save'}
                onOk={!editMode ? onOkAddButtonClickHandler : onOkEditButtonClickHandler}
                onCancel={() => onClose()}
                maskClosable={false}
                closeIcon={false}
                bodyStyle={{ maxHeight: 550, overflow: 'auto' }}
                width={789}
                okButtonProps={{disabled:isUserFetching}}
            >
                {isUserFetching ? (
                    <div className="w-[100%] flex justify-center items-center">
                        {' '}
                        <Spin />
                    </div>
                ) : (
                    <Form layout="vertical" form={form} onValuesChange={handleFormChange}>
                        <Row gutter={[12, 0]}>
                            {[
                                { label: 'First Name', name: 'first_name', type: 'input', required: true },
                                { label: 'Last Name', name: 'last_name', type: 'input', required: true },
                                {
                                    label: 'Email',
                                    name: 'email',
                                    type: 'input',
                                    required: true,
                                    rules: [{ type: 'email' as const, message: 'Enter a valid email address!' }],
                                },
                                { label: 'Phone Number', name: 'primary_mobile', type: 'input', required: true },
                                { label: 'Secondary Phone', name: 'secondary_mobile', type: 'input' },

                                {
                                    label: 'CNIC',
                                    name: 'cnic_number',
                                    type: 'input',
                                    required: true,
                                    rules: [
                                        {
                                            pattern: /^\d{5}-\d{7}-\d$/,
                                            message: 'CNIC format must be XXXXX-XXXXXXX-X.',
                                        },
                                    ],
                                },
                                { label: 'DOB', name: 'dob', type: 'date', required: true, maxLength: 13 },
                                { label: 'Customers status', name: 'customer_status', type: 'select', required: true, options: customerStatusOptions },
                                { label: 'Gender', name: 'gender', type: 'select', required: true, options: genderOptions },
                                { label: 'Province', name: 'province', type: 'select', required: true, options: pakStates },
                                { label: 'City', name: 'city', type: 'select', required: true, options: provinceCities },
                                { label: 'Address', name: 'address_line1', type: 'input', required: true },
                                { label: 'Secondary Address', name: 'address_line2', type: 'input' },
                            ].map(({ label, name, type, required, options, rules, maxLength }) => (
                                <Col span={12} key={name} style={{ marginTop: '0px' }}>
                                    <Item
                                        label={
                                            <HeadingTypography fontSize={14} classNames="flex items-center h-[26px] font-normal">
                                                {label} {required && <span className="text-[#BD273B]"> *</span>}
                                            </HeadingTypography>
                                        }
                                        name={name}
                                        rules={rules}
                                        // validateTrigger="onSubmit"
                                        validateTrigger={['onSubmit', 'onChange', 'onBlur']}
                                    >
                                        {type === 'select' ? (
                                            <Select
                                                showSearch
                                                optionFilterProp="label"
                                                className="lead-detail-selector w-full"
                                                allowClear
                                                placeholder={`Select ${label}`}
                                                filterSort={(optionA, optionB) => {
                                                    return (optionA?.label ?? '')
                                                        .toString()
                                                        .toLowerCase()
                                                        .localeCompare((optionB?.label ?? '').toString().toLowerCase());
                                                }}
                                                options={
                                                    options?.map(option => {
                                                        return {
                                                            value: option.value,
                                                            label: option.label,
                                                        };
                                                    }) || []
                                                }
                                            />
                                        ) : type === 'date' ? (
                                            <DatePicker
                                                onChange={date => {
                                                    form.setFieldValue('dob', date);
                                                }}
                                                style={{ width: '100%' }}
                                                changeOnBlur
                                            />
                                        ) : type === 'input' ? (
                                            <Input maxLength={maxLength} style={{ height: '35px' }} placeholder={label} />
                                        ) : null}
                                    </Item>
                                </Col>
                            ))}
                        </Row>
                    </Form>
                )}
            </Modal>
        </>
    );
};
